import { ComponentConfigType } from '../../utils/data-parser';
import { ComponentType, type IConfig } from '../../types';
import { ToolbarGroups } from '../../../components/Editor/config/registry';
import {
  BASIC_JSON_LOGIC_TOOLTIP,
  BASIC_SELECT_VALUE_TOOLTIP,
  BASIC_SWITCH_VALUE_TOOLTIP,
  BASIC_TANSLATE_TOOLTIP,
  BASIC_VALUE_TOOLTIP,
  BASIC_VARIABLE_TOOLTIP,
} from '../../utils';

export const config: IConfig = {
  component: ComponentType.Editor,
  configGroups: [
    {
      title: 'Component Settings',
      items: [
        {
          name: 'initialValue',
          label: 'Initial Value',
          valueComponent: {
            type: 'Input.TextArea',
          },
          configTypes: [
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
            ComponentConfigType.Variable,
            ComponentConfigType.Name,
            ComponentConfigType.Translate,
          ],
          tooltips: [
            { key: ComponentConfigType.Value, value: BASIC_VALUE_TOOLTIP },
            {
              key: ComponentConfigType.JsonLogic,
              value: BASIC_JSON_LOGIC_TOOLTIP,
            },
            {
              key: ComponentConfigType.Variable,
              value: BASIC_VARIABLE_TOOLTIP,
            },
            {
              key: ComponentConfigType.Translate,
              value: BASIC_TANSLATE_TOOLTIP,
            },
          ],
        },
        {
          name: 'disabled',
          label: 'Disabled',
          valueComponent: { type: 'Switch' },
          configTypes: [
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: [
            {
              key: ComponentConfigType.Value,
              value: BASIC_SWITCH_VALUE_TOOLTIP,
            },
            {
              key: ComponentConfigType.JsonLogic,
              value: BASIC_JSON_LOGIC_TOOLTIP,
            },
          ],
        },
        {
          name: 'visible',
          label: 'Visible',
          valueComponent: { type: 'Switch' },
          configTypes: [
            ComponentConfigType.Variable,
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: [
            {
              key: ComponentConfigType.Variable,
              value: BASIC_VARIABLE_TOOLTIP,
            },
            {
              key: ComponentConfigType.Value,
              value: BASIC_SWITCH_VALUE_TOOLTIP,
            },
            {
              key: ComponentConfigType.JsonLogic,
              value: BASIC_JSON_LOGIC_TOOLTIP,
            },
          ],
        },
        {
          name: 'previewMode',
          label: 'Preview Mode',
          valueComponent: { type: 'Switch' },
          configTypes: [ComponentConfigType.Value],
          tooltips: [
            {
              key: ComponentConfigType.Value,
              value: BASIC_SWITCH_VALUE_TOOLTIP,
            },
          ],
        },
      ],
    },
    {
      title: 'Editor Settings - Core',
      items: [
        {
          name: 'height',
          label: 'Height',
          valueComponent: {
            type: 'InputNumber',
            placeholder: '600',
          },
          configTypes: [ComponentConfigType.Value],
          tooltips: [
            { key: ComponentConfigType.Value, value: BASIC_VALUE_TOOLTIP },
          ],
        },
        {
          name: 'placeholder',
          label: 'Placeholder',
          valueComponent: {
            type: 'Input',
          },
          configTypes: [
            ComponentConfigType.Value,
            ComponentConfigType.Translate,
          ],
          tooltips: [
            { key: ComponentConfigType.Value, value: BASIC_VALUE_TOOLTIP },
            {
              key: ComponentConfigType.Translate,
              value: BASIC_TANSLATE_TOOLTIP,
            },
          ],
        },
        {
          name: 'menubar',
          label: 'Menu Bar',
          valueComponent: {
            type: 'Switch',
          },
          configTypes: [ComponentConfigType.Value],
          tooltips: [
            {
              key: ComponentConfigType.Value,
              value: BASIC_SWITCH_VALUE_TOOLTIP,
            },
          ],
        },
        {
          name: 'statusbar',
          label: 'Status Bar',
          valueComponent: {
            type: 'Switch',
          },
          configTypes: [ComponentConfigType.Value],
          tooltips: [
            {
              key: ComponentConfigType.Value,
              value: BASIC_SWITCH_VALUE_TOOLTIP,
            },
          ],
        },
        {
          name: 'branding',
          label: 'Branding',
          valueComponent: {
            type: 'Switch',
          },
          configTypes: [ComponentConfigType.Value],
          tooltips: [
            {
              key: ComponentConfigType.Value,
              value: BASIC_SWITCH_VALUE_TOOLTIP,
            },
          ],
        },
        {
          name: 'downloadEnabled',
          label: 'Download Enabled',
          valueComponent: { type: 'Switch' },
          configTypes: [
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: [
            {
              key: ComponentConfigType.Value,
              value: BASIC_SWITCH_VALUE_TOOLTIP,
            },
            {
              key: ComponentConfigType.JsonLogic,
              value: BASIC_JSON_LOGIC_TOOLTIP,
            },
          ],
        },
      ],
    },
    {
      title: 'Editor Settings - Toolbar',
      items: [
        {
          name: 'toolbarGroups',
          label: 'Toolbar Groups',
          valueComponent: {
            type: 'Select',
            mode: 'multiple',
            options: [
              { label: 'History (Undo/Redo)', value: ToolbarGroups.History },
              {
                label: 'Formatting (Bold/Italic/Underline/Strikethrough)',
                value: ToolbarGroups.Formatting,
              },
              { label: 'Alignment', value: ToolbarGroups.Alignment },
              { label: 'Lists', value: ToolbarGroups.Lists },
              { label: 'Media (Image)', value: ToolbarGroups.Media },
              { label: 'Link', value: ToolbarGroups.Link },
              { label: 'Table', value: ToolbarGroups.Table },
              {
                label: 'Utility (Code/Fullscreen/Preview)',
                value: ToolbarGroups.Utility,
              },
              {
                label: 'Client Utility (Fullscreen/Preview)',
                value: ToolbarGroups.ClientUtility,
              },
              { label: 'Text Style', value: ToolbarGroups.TextStyle },
              {
                label: 'Colors (Forecolor/Backcolor)',
                value: ToolbarGroups.Colors,
              },
              { label: 'Fonts (Font Family/Size)', value: ToolbarGroups.Fonts },
              { label: 'Indentation', value: ToolbarGroups.Indentation },
              { label: 'Remove Format', value: ToolbarGroups.RemoveFormat },
              {
                label: 'Load from Template',
                value: ToolbarGroups.LoadFromTemplate,
              },
              {
                label: 'Download (PDF/HTML)',
                value: ToolbarGroups.Download,
              },
            ],
          },
          configTypes: [ComponentConfigType.Value],
          tooltips: [
            {
              key: ComponentConfigType.Value,
              value: BASIC_SELECT_VALUE_TOOLTIP,
            },
          ],
        },
      ],
    },
    {
      title: 'Editor Settings - Templates',
      items: [
        {
          name: 'templateEnabled',
          label: 'Enabled',
          valueComponent: { type: 'Switch' },
          configTypes: [
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: [
            {
              key: ComponentConfigType.Value,
              value: BASIC_SWITCH_VALUE_TOOLTIP,
            },
            {
              key: ComponentConfigType.JsonLogic,
              value: BASIC_JSON_LOGIC_TOOLTIP,
            },
          ],
        },
        {
          name: 'templateVisible',
          label: 'Visible',
          valueComponent: { type: 'Switch' },
          configTypes: [
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: [
            {
              key: ComponentConfigType.Value,
              value: BASIC_SWITCH_VALUE_TOOLTIP,
            },
            {
              key: ComponentConfigType.JsonLogic,
              value: BASIC_JSON_LOGIC_TOOLTIP,
            },
          ],
        },
        {
          name: 'templateIds',
          label: 'Load from Templates',
          valueComponent: { type: 'TemplateSelect' },
          configTypes: [
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: [
            { key: ComponentConfigType.Value, value: BASIC_VALUE_TOOLTIP },
            {
              key: ComponentConfigType.JsonLogic,
              value: BASIC_JSON_LOGIC_TOOLTIP,
            },
          ],
        },
        {
          name: 'templateDataMapping',
          label: 'Data Mapping',
          valueComponent: {
            type: 'CodeEditor',
            placeholder: 'JMESPath expression for data mapping',
          },
          configTypes: [
            ComponentConfigType.Value,
            ComponentConfigType.JsonLogic,
          ],
          tooltips: [
            { key: ComponentConfigType.Value, value: BASIC_VALUE_TOOLTIP },
            {
              key: ComponentConfigType.JsonLogic,
              value: BASIC_JSON_LOGIC_TOOLTIP,
            },
          ],
        },
      ],
    },
    {
      title: 'Component Styles',
      component: 'CssSettings',
    },
  ],
};
